import { Injectable, computed, effect, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

export interface ThemeConfig {
  lightClass: string;
  darkClass: string;
  storageKey: string;
  useSystemPreference: boolean;
}

const DEFAULT_CONFIG: ThemeConfig = {
  lightClass: 'tds-mode-light',
  darkClass: 'tds-mode-dark',
  storageKey: 'app_theme_mode',
  useSystemPreference: false,
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly cfg: ThemeConfig = DEFAULT_CONFIG;

  private readonly _mode = signal<ThemeMode>(this.resolveInitialMode());

  readonly mode = this._mode.asReadonly();

  readonly isDark = computed(() => this._mode() === 'dark');
  readonly themeClass = computed(() =>
    this._mode() === 'dark' ? this.cfg.darkClass : this.cfg.lightClass,
  );

  constructor() {
    effect(() => {
      const mode = this._mode();
      this.persist(mode);
    });
  }

  setMode(mode: ThemeMode): void {
    if (mode === this._mode()) return;
    this._mode.set(mode);
  }

  toggle(): void {
    this._mode.update((m) => (m === 'dark' ? 'light' : 'dark'));
  }

  private resolveInitialMode(): ThemeMode {
    // 1) storage
    const stored = this.safeGetStorage(this.cfg.storageKey);
    if (stored === 'light' || stored === 'dark') return stored;

    // 2) system preference
    if (this.cfg.useSystemPreference && typeof window !== 'undefined') {
      const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
      return prefersDark ? 'dark' : 'light';
    }

    // 3) default
    return 'light';
  }

  private persist(mode: ThemeMode): void {
    try {
      localStorage.setItem(this.cfg.storageKey, mode);
    } catch {
      // storage pode falhar (privacy mode, etc.) — silencioso
    }
  }

  private safeGetStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}
